import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Snackbar from '@mui/material/Snackbar';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ImageIcon from '@mui/icons-material/Image';
import ImageUploadDialog from '../../components/ImageUploadDialog';
import { selectAllArticleTypes } from './ArticleTypeSlice';

// The record's fields, in the order they are shown in the backoffice and on the
// public page. Keys must match METADATA_FIELDS in the server's renderer — that
// is the contract between this form and the templates. Adding a field means
// adding it in both places.
const FIELDS = [
    { key: 'pais', label: 'País', required: true },
    { key: 'cidade', label: 'Cidade', required: true },
    { key: 'morada', label: 'Morada', required: true, multiline: true },
    { key: 'tipoSuporte', label: 'Tipo de suporte', required: true },
    { key: 'periodo', label: 'Período', required: true, placeholder: 'ex.: Anos 60–70' },
    { key: 'contribuidor', label: 'Contribuidor', required: false },
];

const emptyForm = () => ({
    title: '',
    ...FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: '' }), {}),
});

// Pick a filename to preview a hydrated cover image. The API returns the image
// with its responsive `imageFiles` (e.g. "x-320w.webp"); `name` is the logical
// image, not a file on disk, so preferring a mid size is what makes the
// preview actually load. Mirrors pickCoverFilename in ArticleEditor.
const pickCoverFilename = (coverImage) => {
    const files = (coverImage && coverImage.imageFiles) || [];
    if (!files.length) return (coverImage && coverImage.name) || '';
    const sorted = [...files].sort((a, b) => a.width - b.width);
    const mid = sorted.find((f) => f.width >= 320) || sorted[sorted.length - 1];
    return mid.name;
};

// A "ficha" of the Projeto Lojas section: a fielded record, not a rich-text
// article. It saves through the same /api/articles endpoint as everything else —
// the store name goes in `title` and the rest in `metadata` — so listings,
// search, versioning and publishing all behave identically.
const EtiquetaEditor = ({ initialContent, articleTypeId, onSave, onUpload, onSaveSuccess, loading }) => {
    const articleTypes = useSelector(selectAllArticleTypes);

    const [form, setForm] = useState(emptyForm);
    const [coverImage, setCoverImage] = useState(null); // { id, filename, alt }
    const [idArticle, setIdArticle] = useState(null);
    const [idArticleType, setIdArticleType] = useState(articleTypeId || null);
    const [visible, setVisible] = useState(false);
    const [saving, setSaving] = useState(false);
    const [dialog, setDialog] = useState({ open: false, file: null });
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const showSnackbar = useCallback((message, severity = 'success') => setSnackbar({ open: true, message, severity }), []);

    // Types that use this form — the picker only offers those, so a ficha can
    // never be filed under an article type by accident.
    const etiquetaTypes = useMemo(() => (articleTypes || []).filter((t) => t.form === 'etiqueta'), [articleTypes]);

    // Load an existing ficha into the form.
    useEffect(() => {
        if (!initialContent) {
            setForm(emptyForm());
            setCoverImage(null);
            setIdArticle(null);
            setVisible(false);
            return;
        }

        const metadata = initialContent.metadata || {};
        setForm({
            title: initialContent.title || '',
            ...FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: metadata[f.key] || '' }), {}),
        });
        setIdArticle(initialContent.id || initialContent.idArticle || null);
        setVisible(Boolean(initialContent.visible ?? initialContent.isVisible));
        if (initialContent.idArticleType) setIdArticleType(initialContent.idArticleType);

        const cover = initialContent.coverImage;
        setCoverImage(cover ? { id: cover.idImage, filename: pickCoverFilename(cover), alt: cover.alt || '' } : null);
    }, [initialContent]);

    // Default the type once the list arrives (new ficha).
    useEffect(() => {
        if (!idArticleType && etiquetaTypes.length) setIdArticleType(etiquetaTypes[0].idArticleType);
    }, [idArticleType, etiquetaTypes]);

    const setField = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));

    // --- cover upload (mandatory for a ficha) ---
    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (!file) return;
        const maxBytes = (parseInt(process.env.REACT_APP_MAX_IMAGE_SIZE_MB, 10) || 10) * 1024 * 1024;
        const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
        if (!allowed.includes(file.type)) return showSnackbar(`Tipo de ficheiro inválido: ${allowed.join(', ')}`, 'error');
        if (file.size > maxBytes) return showSnackbar(`Ficheiro demasiado grande (máx. ${maxBytes / 1024 / 1024}MB)`, 'error');
        event.target.value = ''; // re-picking the same file must fire onChange again
        setDialog({ open: true, file });
    };

    const handleUploadSubmit = async ({ filename, alt }) => {
        const { file } = dialog;
        if (!file || !onUpload) return;
        try {
            file.options = { filename, alt };
            const image = await onUpload(file);
            if (image) {
                setCoverImage({ id: image.id, filename: image.filename, alt: image.alt });
                showSnackbar('Imagem da etiqueta carregada', 'success');
            }
        } catch (error) {
            showSnackbar(error.message || 'Erro ao carregar imagem', 'error');
        } finally {
            setDialog({ open: false, file: null });
        }
    };

    // Publishing needs every required field and the image; a draft needs only a name.
    const missing = FIELDS.filter((f) => f.required && !String(form[f.key] || '').trim()).map((f) => f.label);
    const canPublish = !!form.title.trim() && !missing.length && !!coverImage;

    const coverPreviewSrc = coverImage
        ? coverImage.filename && coverImage.filename.startsWith('http')
            ? coverImage.filename
            : `${process.env.PUBLIC_URL || ''}/images/articles/${coverImage.filename}`
        : '';

    const handleSave = async (publish) => {
        if (!form.title.trim()) return showSnackbar('O nome da loja é obrigatório', 'error');
        if (publish && !coverImage) return showSnackbar('A imagem da etiqueta é obrigatória para publicar', 'error');
        if (publish && missing.length) return showSnackbar(`Faltam campos: ${missing.join(', ')}`, 'error');

        const metadata = FIELDS.reduce((acc, f) => {
            const value = String(form[f.key] || '').trim();
            if (value) acc[f.key] = value;
            return acc;
        }, {});

        setSaving(true);
        try {
            // No `content`: the server fills in an empty Draft.js body for
            // metadata-bearing records (see controllers/article.js upsert).
            const saved = await onSave({
                idArticle,
                idArticleType,
                title: form.title.trim(),
                metadata,
                coverImageId: coverImage ? coverImage.id : null,
                visible: publish,
                isDraft: !publish,
            });

            if (saved) {
                if (saved.idArticle && !idArticle) setIdArticle(saved.idArticle);
                setVisible(publish);
                showSnackbar(publish ? 'Ficha publicada' : 'Rascunho guardado', 'success');
                if (publish && onSaveSuccess) setTimeout(() => onSaveSuccess(), 600);
            } else {
                showSnackbar('Não foi possível guardar a ficha', 'error');
            }
        } catch (error) {
            showSnackbar(error.message || 'Erro ao guardar', 'error');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <Box sx={{ p: 3 }}>A carregar…</Box>;

    return (
        <Box sx={{ p: { xs: 1.5, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2, width: '100%', maxWidth: 900, mx: 'auto' }}>
            {/* One column on a phone, form-beside-image on a desktop. The old
                `flex: 0 0 240px` image column forced a horizontal split at every
                width, which left the fields about 80px wide on a handset. */}
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 260px' }, alignItems: 'start' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                    <TextField
                        label='Nome da loja'
                        value={form.title}
                        onChange={setField('title')}
                        required
                        fullWidth
                        helperText='Serve de título da ficha e define o endereço da página'
                    />

                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
                        {FIELDS.map((field) => (
                            <TextField
                                key={field.key}
                                label={field.label}
                                value={form[field.key]}
                                onChange={setField(field.key)}
                                required={field.required}
                                placeholder={field.placeholder}
                                multiline={field.multiline}
                                minRows={field.multiline ? 2 : undefined}
                                fullWidth
                                helperText={field.required ? undefined : 'Opcional'}
                                sx={field.multiline ? { gridColumn: '1 / -1' } : undefined}
                            />
                        ))}
                    </Box>

                    {etiquetaTypes.length > 1 && (
                        <TextField
                            select
                            label='Secção'
                            value={idArticleType || ''}
                            onChange={(e) => setIdArticleType(Number(e.target.value))}
                            SelectProps={{ native: true }}
                            InputLabelProps={{ shrink: true }}
                            sx={{ maxWidth: { sm: 260 } }}
                        >
                            {etiquetaTypes.map((t) => (
                                <option key={t.idArticleType} value={t.idArticleType}>
                                    {t.display_name || t.name}
                                </option>
                            ))}
                        </TextField>
                    )}
                </Box>

                {/* Cover: mandatory for a ficha — the etiqueta itself is the content. */}
                <Paper variant='outlined' sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box
                        sx={{
                            height: { xs: 160, md: 200 },
                            border: '1px',
                            borderStyle: coverImage ? 'solid' : 'dashed',
                            borderColor: coverImage ? 'divider' : 'error.light',
                            borderRadius: 1.5,
                            bgcolor: '#f4f1ea',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            p: 1,
                        }}
                    >
                        {coverImage ? (
                            <Box
                                component='img'
                                src={coverPreviewSrc}
                                alt={coverImage.alt || 'Etiqueta'}
                                // Contain, never cover: the etiqueta is shown whole
                                // on the site, so the preview must not crop it.
                                sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                            />
                        ) : (
                            <Box sx={{ textAlign: 'center', color: 'error.main' }}>
                                <ImageIcon />
                                <Typography variant='caption' component='div'>
                                    Imagem obrigatória
                                </Typography>
                            </Box>
                        )}
                    </Box>
                    <Button variant='outlined' component='label' fullWidth>
                        {coverImage ? 'Substituir imagem' : 'Carregar imagem'}
                        <input type='file' hidden accept='image/*' onChange={handleFileSelect} />
                    </Button>
                    <Typography variant='caption' color='text.secondary'>
                        A etiqueta é mostrada inteira (sem corte), por isso pode ser pequena ou não quadrada.
                    </Typography>
                </Paper>
            </Box>

            {/* Sticky actions, matching the article editor. */}
            <Box
                sx={{
                    position: 'sticky',
                    bottom: 0,
                    zIndex: 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexWrap: 'wrap',
                    px: 1.5,
                    py: 1.25,
                    pb: 'calc(10px + env(safe-area-inset-bottom))',
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    boxShadow: '0 -2px 12px rgba(0,0,0,0.06)',
                }}
            >
                <Chip
                    size='small'
                    label={visible ? 'Publicada' : 'Não publicada'}
                    color={visible ? 'success' : 'default'}
                    variant={visible ? 'filled' : 'outlined'}
                />
                {!canPublish && (
                    <Typography variant='caption' color='text.secondary'>
                        Para publicar: {[...missing, ...(coverImage ? [] : ['Imagem'])].join(', ') || 'preencha o nome'}
                    </Typography>
                )}
                <Box sx={{ flex: 1 }} />
                <Button variant='outlined' color='inherit' onClick={() => handleSave(false)} disabled={saving}>
                    Guardar rascunho
                </Button>
                <Button variant='contained' onClick={() => handleSave(true)} disabled={saving || !canPublish}>
                    Publicar
                </Button>
            </Box>

            <ImageUploadDialog
                open={dialog.open}
                onClose={() => setDialog({ open: false, file: null })}
                file={dialog.file}
                onSubmit={handleUploadSubmit}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                    severity={snackbar.severity}
                    variant='filled'
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default EtiquetaEditor;
